import { z } from 'zod';

// Combos and bracket estimates from Commander Spellbook
// (https://commanderspellbook.com), per deck.

/**
 * Spellbook's bracket tags and the official bracket each stands for, as its
 * source maps them (backend/spellbook/models/variant.py). It never estimates 5.
 */
export const BRACKET_TAGS = {
  E: { bracket: 1, label: 'Exhibition' },
  C: { bracket: 2, label: 'Core' },
  O: { bracket: 2, label: 'Oddball' },
  P: { bracket: 3, label: 'Powerful' },
  S: { bracket: 3, label: 'Spicy' },
  R: { bracket: 4, label: 'Ruthless' },
  B: { bracket: null, label: 'Banned card' },
} as const;
export const BracketTag = z.enum(['E', 'C', 'O', 'P', 'S', 'R', 'B']);
export type BracketTag = z.infer<typeof BracketTag>;

/** Where a copy of a missing combo piece is, other than the deck in question. */
export const OwnedCopy = z.object({
  locationId: z.string(),
  locationName: z.string(),
  kind: z.enum(['drawer', 'deck', 'intake']),
  /** The divider it files behind, when it's in its own drawer. */
  divider: z.string().nullable(),
  quantity: z.number(),
  finish: z.string(),
  /** "Foil", "Etched", "Surge foil"…; null for non-foil. */
  foil: z.string().nullable(),
  setCode: z.string(),
  collectorNumber: z.string(),
});
export type OwnedCopy = z.infer<typeof OwnedCopy>;

const Combo = z.object({
  /** Spellbook's id; the combo's page is https://commanderspellbook.com/combo/<id>/ */
  id: z.string(),
  cards: z.array(z.string()),
  /** Other requirements, e.g. "A creature with an ETB ability". */
  requires: z.array(z.string()),
  produces: z.array(z.string()),
  bracketTag: BracketTag.nullable(),
  popularity: z.number().nullable(),
});

export const DeckInsights = z.object({
  fetchedAt: z.string(),
  /** True when this is an older answer kept because Spellbook couldn't be reached for the current list. */
  outdated: z.boolean(),
  bracket: z.object({
    tag: BracketTag,
    /** Why: the cards and combos that set the estimate, most serious first. */
    reasons: z.array(z.string()),
  }),
  flagged: z.object({
    gameChangers: z.array(z.string()),
    extraTurns: z.array(z.string()),
    massLandDenial: z.array(z.string()),
    banned: z.array(z.string()),
  }),
  /** Combos the deck already has. */
  combos: z.array(Combo.extend({ fastTwoCard: z.boolean() })),
  /** Combos one card short, with where you own that card (if you do). */
  nearMisses: z.array(
    Combo.extend({
      missing: z.object({ name: z.string(), oracleId: z.string().nullable() }),
      owned: z.array(OwnedCopy),
    }),
  ),
  /** Deck cards Spellbook didn't recognise (so they count for nothing above). */
  unmatched: z.array(z.string()),
});
export type DeckInsights = z.infer<typeof DeckInsights>;

/** The name Spellbook knows a card by: both faces, unless they're the same card twice. */
export function spellbookName(name: string): string {
  const faces = name.split(' // ');
  return faces.every((f) => f === faces[0]) ? faces[0]! : name;
}
